// Validated read snapshots for explicitly opted-in desktop routes. Like the
// Trends repository, this cache is memory-only and owned by an auth session.
const entries = new Map();
const revisions = new Map();
const reads = new Map();
const pendingMutations = new Map();
const denied = new Set();
const authorities = new Map();
const views = new Map();
const viewRevisions = new Map();
let epoch = 0;
let nextRead = 0;
let nextRevision = 0;
let nextViewRevision = 0;
const MAX_ENTRIES = 40;
const copy = value => JSON.parse(JSON.stringify(value));
const owner = scope => { try { return JSON.parse(scope); } catch { return null; } };
const authority = scope => JSON.stringify(owner(scope)?.slice(0, 2));
const keyFor = (scope, resource) => JSON.stringify([scope, resource]);

export const desktopTransactionScope = ({ sessionScope, householdId, month }) => (
  sessionScope && /^[1-9]\d*$/.test(String(householdId)) && Number.isSafeInteger(Number(householdId))
    && /^\d{4}-(0[1-9]|1[0-2])$/.test(month || '')
    ? JSON.stringify([String(sessionScope), String(householdId), month]) : null
);
export const desktopSnapshotAllowed = scope => !scope || !denied.has(authority(scope));
export const desktopSnapshotAuthority = scope => {
  if (!scope) return null;
  const key = authority(scope);
  if (!authorities.has(key)) authorities.set(key, {});
  while (authorities.size > MAX_ENTRIES * 2) {
    const oldest = authorities.keys().next().value;
    authorities.delete(oldest);
    denied.delete(oldest);
    for (const [entryKey, entry] of entries) if (authority(entry.scope) === oldest) entries.delete(entryKey);
  }
  return authorities.get(key);
};
export const desktopSnapshotPublicationAllowed = (scope, publicationAuthority) => !scope || (
  desktopSnapshotAllowed(scope) && publicationAuthority === authorities.get(authority(scope))
);
export const readDesktopSnapshot = (scope, resource) => {
  if (!scope || !desktopSnapshotAllowed(scope) || pendingMutations.get(owner(scope)?.[1])) return null;
  const key = keyFor(scope, resource);
  const found = entries.get(key);
  if (!found) return null;
  entries.delete(key);
  entries.set(key, found);
  return copy(found.data);
};
export const beginDesktopSnapshotRead = (scope, resource) => {
  if (!scope) return null;
  const key = keyFor(scope, resource);
  const sequence = ++nextRead;
  if (!revisions.has(scope)) revisions.set(scope, ++nextRevision);
  reads.set(key, sequence);
  while (reads.size > MAX_ENTRIES * 2) {
    const oldest = reads.keys().next().value;
    reads.delete(oldest);
    entries.delete(oldest);
  }
  for (const known of revisions.keys()) {
    if (![...reads.keys()].some(read => JSON.parse(read)[0] === known)) revisions.delete(known);
  }
  return { scope, resource, key, sequence, epoch, revision: revisions.get(scope), authority: desktopSnapshotAuthority(scope) };
};
export const desktopSnapshotReadCurrent = ticket => !ticket || (
  ticket.epoch === epoch && ticket.authority === authorities.get(authority(ticket.scope))
    && ticket.revision === revisions.get(ticket.scope)
    && reads.get(ticket.key) === ticket.sequence
);
export const desktopSnapshotGenerationCurrent = ticket => !ticket || (ticket.epoch === epoch
  && ticket.authority === authorities.get(authority(ticket.scope)) && ticket.revision === revisions.get(ticket.scope));
export const readDesktopTransactionView = scope => scope && desktopSnapshotAllowed(scope) ? copy(views.get(scope) || {}) : {};
export const desktopTransactionViewLease = scope => {
  if (scope && !viewRevisions.has(scope)) viewRevisions.set(scope, ++nextViewRevision);
  while (viewRevisions.size > MAX_ENTRIES) {
    const oldest = viewRevisions.keys().next().value;
    viewRevisions.delete(oldest);
    views.delete(oldest);
  }
  return { scope, epoch, revision: viewRevisions.get(scope) };
};
export const saveDesktopTransactionView = (lease, patch) => {
  if (!lease.scope || lease.epoch !== epoch || lease.revision !== viewRevisions.get(lease.scope)
    || !desktopSnapshotAllowed(lease.scope)) return;
  const previous = views.get(lease.scope) || {};
  views.delete(lease.scope);
  views.set(lease.scope, { ...previous, ...patch });
  while (views.size > MAX_ENTRIES) views.delete(views.keys().next().value);
};
export const commitDesktopSnapshot = (ticket, data) => {
  if (!ticket || !desktopSnapshotReadCurrent(ticket) || pendingMutations.get(owner(ticket.scope)?.[1])) return;
  denied.delete(authority(ticket.scope));
  entries.delete(ticket.key);
  entries.set(ticket.key, { scope: ticket.scope, data: copy(data) });
  while (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value);
};
export const invalidateDesktopTransactionSnapshots = (householdId, { monthKeys, resources } = {}) => {
  const months = Array.isArray(monthKeys) ? monthKeys.filter(month => /^\d{4}-(0[1-9]|1[0-2])$/.test(month)) : [];
  const matches = scope => String(owner(scope)?.[1]) === String(householdId)
    && (!months.length || months.includes(owner(scope)?.[2]));
  if (resources?.length) {
    for (const key of reads.keys()) {
      const [scope, resource] = JSON.parse(key);
      if (matches(scope) && resources.includes(resource)) { reads.set(key, ++nextRead); entries.delete(key); }
    }
    return;
  }
  const scopes = new Set([...revisions.keys(), ...entries.values()].map(value => typeof value === 'string' ? value : value.scope));
  for (const key of reads.keys()) scopes.add(JSON.parse(key)[0]);
  for (const scope of scopes) if (matches(scope)) revisions.set(scope, ++nextRevision);
  for (const [key, value] of entries) if (matches(value.scope)) entries.delete(key);
};
export const denyDesktopSnapshotScope = scope => {
  if (!scope) return;
  denied.add(authority(scope));
  authorities.set(authority(scope), {});
  invalidateDesktopTransactionSnapshots(owner(scope)?.[1]);
  // An evicted refusal has no reusable financial entries or valid earlier reads.
  // A future visit must perform fresh authenticated reads before showing data.
  while (denied.size > MAX_ENTRIES) denied.delete(denied.values().next().value);
};
export const beginDesktopSnapshotMutation = householdId => {
  const key = String(householdId);
  const startedEpoch = epoch;
  pendingMutations.set(key, (pendingMutations.get(key) || 0) + 1);
  invalidateDesktopTransactionSnapshots(key);
  return () => {
    if (startedEpoch !== epoch) return;
    const remaining = (pendingMutations.get(key) || 1) - 1;
    if (remaining) pendingMutations.set(key, remaining); else pendingMutations.delete(key);
    invalidateDesktopTransactionSnapshots(key);
  };
};
export const clearDesktopTransactionSnapshots = () => {
  epoch += 1;
  entries.clear(); revisions.clear(); reads.clear(); pendingMutations.clear(); denied.clear(); authorities.clear();
  views.clear(); viewRevisions.clear();
};
export const clearDesktopSnapshotsForHousehold = householdId => {
  invalidateDesktopTransactionSnapshots(householdId);
  const scopes = new Set([...views.keys(), ...viewRevisions.keys(), ...[...reads.keys()].map(key => JSON.parse(key)[0])]);
  for (const scope of scopes) {
    if (owner(scope)?.[1] !== String(householdId)) continue;
    views.delete(scope);
    viewRevisions.delete(scope);
  }
};
export const clearDesktopSnapshotsExceptSession = sessionScope => {
  for (const key of reads.keys()) {
    const scope = JSON.parse(key)[0];
    if (owner(scope)?.[0] !== String(sessionScope)) revisions.set(scope, ++nextRevision);
  }
  for (const [key, value] of entries) if (owner(value.scope)?.[0] !== String(sessionScope)) entries.delete(key);
  for (const scope of viewRevisions.keys()) if (owner(scope)?.[0] !== String(sessionScope)) {
    views.delete(scope);
    viewRevisions.delete(scope);
  }
  for (const deniedAuthority of denied) if (owner(deniedAuthority)?.[0] !== String(sessionScope)) denied.delete(deniedAuthority);
  for (const key of authorities.keys()) if (owner(key)?.[0] !== String(sessionScope)) authorities.delete(key);
};
export const isDesktopSnapshotMutation = (endpoint, method = 'GET') => (
  !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())
    && /^\/(transactions(?:\/|$)|reviewed-transactions(?:\/|$)|categories(?:\/|$)|budgets(?:\/|$)|income(?:\/|$)|households\/\d+\/(?:income|budget)|net-worth(?:\/|$)|plaid(?:\/|$)|bank(?:\/|$)|sync_transactions(?:\/|$))/.test(endpoint)
);
